import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/cn";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function parseDateValue(value) {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export function toDateValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function formatDateDisplay(value) {
  const date = parseDateValue(value);
  if (!date) return "Select date";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  return cells;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function DateCalendar({
  value = "",
  onChange,
  disabled = false,
  minDate,
  className,
}) {
  const selected = parseDateValue(value);
  const today = new Date();
  const minDay = minDate ? startOfDay(minDate) : null;
  const [viewYear, setViewYear] = useState(
    (selected || today).getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState((selected || today).getMonth());

  useEffect(() => {
    const base = selected || today;
    setViewYear(base.getFullYear());
    setViewMonth(base.getMonth());
  }, [value]);

  const calendarDays = useMemo(
    () => buildCalendarDays(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const shiftMonth = (delta) => {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  };

  const todayValue = toDateValue(today);
  const monthLabel = `${MONTHS[viewMonth]} ${viewYear}`;

  const jumpToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    if (!disabled) onChange?.(todayValue);
  };

  return (
    <div className={className}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          disabled={disabled}
          onClick={() => shiftMonth(-1)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted/70 hover:text-foreground disabled:opacity-60"
        >
          <ChevronLeft size={16} />
        </button>
        <p
          className="text-sm font-semibold text-foreground"
          aria-live="polite"
          aria-atomic="true"
        >
          {monthLabel}
        </p>
        <button
          type="button"
          aria-label="Next month"
          disabled={disabled}
          onClick={() => shiftMonth(1)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:bg-muted/70 hover:text-foreground disabled:opacity-60"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="mb-2 flex justify-end">
        <button
          type="button"
          disabled={disabled}
          onClick={jumpToToday}
          className="rounded-md px-2 py-1 text-xs font-medium text-brand hover:bg-brand/10 disabled:opacity-60"
        >
          Today
        </button>
      </div>

      <div
        className="grid grid-cols-7 gap-1 text-center"
        role="grid"
        aria-label={monthLabel}
      >
        {WEEKDAYS.map((day) => (
          <span
            key={day}
            role="columnheader"
            className="py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {day}
          </span>
        ))}
        {calendarDays.map((day, index) => {
          if (!day) {
            return <span key={`empty-${index}`} aria-hidden />;
          }

          const cellDate = new Date(viewYear, viewMonth, day);
          const cellValue = toDateValue(cellDate);
          const isSelected = value === cellValue;
          const isToday = todayValue === cellValue;
          const isDisabled =
            disabled || (minDay ? startOfDay(cellDate) < minDay : false);

          return (
            <button
              key={cellValue}
              type="button"
              role="gridcell"
              aria-selected={isSelected}
              aria-current={isToday ? "date" : undefined}
              aria-disabled={isDisabled}
              disabled={isDisabled}
              onClick={() => onChange?.(cellValue)}
              className={cn(
                "h-10 w-full rounded-lg text-sm transition-colors",
                isSelected
                  ? "bg-brand text-white font-semibold"
                  : "text-foreground hover:bg-muted/70",
                isToday && !isSelected && "ring-1 ring-brand/40",
                isDisabled &&
                  "cursor-not-allowed text-muted-foreground/50 hover:bg-transparent",
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
